import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import BlogLayout from '../blog/BlogLayout';
import HittingPercentageCalculator from '../blog/HittingPercentageCalculator';
import MetaTags from '../common/MetaTags';

export default function BlogPostView({ slug, onNavigate }) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    loadPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (post && post.content) {
      // Execute any scripts in the blog post content after a short delay to ensure DOM is ready
      setTimeout(() => {
        const contentElement = document.querySelector('.blog-content');
        if (contentElement) {
          const scripts = contentElement.querySelectorAll('script');
          scripts.forEach(script => {
            const newScript = document.createElement('script');
            if (script.src) {
              newScript.src = script.src;
            } else {
              newScript.textContent = script.textContent;
            }
            document.body.appendChild(newScript);
          });
        }
      }, 100);
    }
  }, [post]);

  async function loadPost() {
    setLoading(true);
    setError(false);
    try {
      const q = query(
        collection(db, 'blog_posts'),
        where('slug', '==', slug),
        where('published', '==', true)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setError(true);
      } else {
        setPost({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() });
      }
    } catch (err) {
      console.error('Error loading post:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  function formatDate(timestamp) {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  if (loading) {
    return (
      <BlogLayout>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: 'var(--muted)' }}>
            Loading...
          </div>
        </div>
      </BlogLayout>
    );
  }

  if (error || !post) {
    return (
      <BlogLayout>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <h1 style={{ fontSize: '32px', marginBottom: '16px' }}>Post Not Found</h1>
          <p style={{ color: 'var(--muted)', marginBottom: '24px' }}>
            The post you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => window.location.href = '/blog'}
            className="btn btn-primary"
          >
            ← Back to Blog
          </button>
        </div>
      </BlogLayout>
    );
  }

  // Helper function to strip HTML tags from excerpt
  function stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  // Generate clean description from excerpt or content
  const metaDescription = post.excerpt
    ? stripHtml(post.excerpt).substring(0, 160)
    : stripHtml(post.content).substring(0, 160);

  return (
    <BlogLayout>
      <MetaTags
        title={post.title}
        description={metaDescription}
        image={post.featuredImage}
        url={window.location.href}
        type="article"
        author={post.author}
        publishedTime={post.createdAt?.toDate?.()?.toISOString()}
        modifiedTime={post.updatedAt?.toDate?.()?.toISOString()}
        section={post.categoryName}
      />
      <style>{`
        .blog-post-article {
          max-width: 800px;
          margin: 0 auto;
          padding: 40px 20px;
        }

        .blog-post-h1 {
          font-size: 48px;
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 16px;
          color: #1a1a1a;
        }

        .blog-post-featured-image {
          width: 100%;
          max-height: 500px;
          border-radius: 12px;
          overflow: hidden;
          margin-bottom: 40px;
        }

        .blog-post-cta {
          margin-top: 60px;
          padding: 32px;
          background: linear-gradient(135deg, rgba(46, 90, 172, 0.1) 0%, rgba(46, 90, 172, 0.05) 100%);
          border-radius: 12px;
          text-align: center;
        }

        @media (max-width: 768px) {
          .blog-post-article {
            padding: 24px 16px;
          }

          .blog-post-h1 {
            font-size: 32px;
          }

          .blog-post-featured-image {
            margin-bottom: 24px;
          }

          .blog-post-cta {
            margin-top: 40px;
            padding: 24px;
          }
        }

        @media (max-width: 480px) {
          .blog-post-article {
            padding: 20px 12px;
          }

          .blog-post-h1 {
            font-size: 28px;
          }

          .blog-post-cta {
            padding: 20px;
          }
        }
      `}</style>
      <article className="blog-post-article">
        {/* Back Button */}
        <button
          onClick={() => window.location.href = '/blog'}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--primary)',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '32px',
            padding: '8px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          ← Back to Blog
        </button>

        {/* Category Badge */}
        {post.categoryName && (
          <div style={{
            display: 'inline-block',
            padding: '6px 16px',
            borderRadius: '16px',
            background: 'rgba(46, 90, 172, 0.1)',
            color: 'var(--primary)',
            fontSize: '13px',
            fontWeight: '600',
            marginBottom: '20px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {post.categoryName}
          </div>
        )}

        {/* Title */}
        <h1 className="blog-post-h1">
          {post.title}
        </h1>

        {/* Meta Info */}
        <div style={{
          fontSize: '14px',
          color: 'var(--muted)',
          marginBottom: '32px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <span>{formatDate(post.createdAt)}</span>
          {post.readTime && (
            <>
              <span>•</span>
              <span>{post.readTime} min read</span>
            </>
          )}
          {post.author && (
            <>
              <span>•</span>
              <span>By {post.author}</span>
            </>
          )}
        </div>

        {/* Featured Image */}
        {post.featuredImage && (
          <div className="blog-post-featured-image">
            <img
              src={post.featuredImage}
              alt={post.title}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
          </div>
        )}

        {/* Content */}
        <div
          style={{
            fontSize: '18px',
            lineHeight: '1.8',
            color: '#333'
          }}
          className="blog-content"
        >
          {post.content && post.content.includes('[HITTING_PERCENTAGE_CALCULATOR]') ? (
            // Split content and inject calculator
            post.content.split('[HITTING_PERCENTAGE_CALCULATOR]').map((part, index) => (
              <div key={index}>
                <div dangerouslySetInnerHTML={{ __html: part }} />
                {index < post.content.split('[HITTING_PERCENTAGE_CALCULATOR]').length - 1 && (
                  <HittingPercentageCalculator />
                )}
              </div>
            ))
          ) : (
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          )}
        </div>

        {/* CTA Section */}
        <div className="blog-post-cta">
          <h3 style={{
            fontSize: '24px',
            fontWeight: '700',
            marginBottom: '12px',
            color: '#1a1a1a'
          }}>
            Ready to Track Your Stats?
          </h3>
          <p style={{
            fontSize: '16px',
            color: 'var(--muted)',
            marginBottom: '24px'
          }}>
            Start tracking your volleyball performance for free today.
          </p>
          <a
            href="/?signup=true"
            className="btn btn-primary"
            style={{ padding: '14px 32px', fontSize: '16px', textDecoration: 'none', display: 'inline-block' }}
          >
            Get Started Free →
          </a>
        </div>

        {/* Back to Blog */}
        <div style={{ marginTop: '40px', textAlign: 'center' }}>
          <button
            onClick={() => window.location.href = '/blog'}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--primary)',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              padding: '8px 0'
            }}
          >
            ← Back to Blog
          </button>
        </div>
      </article>
    </BlogLayout>
  );
}
